"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { FileText } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { can } from "@/utils/service";
import { money, shortDate } from "@/utils/api";
import ListShell, { toolbarInput, pill } from "@/components/crm/ListShell";
import { useCounts, countText } from "@/components/crm/useCounts";
import { useRemoteList } from "@/components/crm/useRemoteList";
import { QUOTE_STATUS } from "./quoteStatus";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";

interface Quote {
  id: string; number: string; version: number; title: string; status: string; issue_date: string; expiry_date: string | null;
  currency: string; total: string; account?: { name: string } | null; owner?: { name: string } | null;
}

export default function QuotesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("-issue_date");
  const list = useRemoteList<Quote>("/quotes", { search: query, status, sort });
  const { counts, loading: countsLoading } = useCounts("/quotes", {
    all: {}, pending: { status: "pending_approval" }, sent: { status: "sent" }, accepted: { status: "accepted,converted" },
  }, list.rows.length);

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const columns: ColumnDef<Quote>[] = [
    { accessorKey: "number", header: "Quote", cell: ({ row }) => (<div><p className="text-xs font-semibold text-gray-900">{row.original.number}{row.original.version > 1 && <span className="text-gray-500"> v{row.original.version}</span>}</p><p className="text-xs text-gray-500">{row.original.title}</p></div>) },
    { id: "account", header: "Account", cell: ({ row }) => <span className="text-xs text-gray-700">{row.original.account?.name ?? "—"}</span> },
    { accessorKey: "total", header: "Total", cell: ({ row }) => <span className="text-xs font-semibold text-gray-900">{money(row.original.total, row.original.currency)}</span> },
    { id: "dates", header: "Issued / Valid until", cell: ({ row }) => <span className="text-xs text-gray-600">{shortDate(row.original.issue_date)} → {shortDate(row.original.expiry_date)}</span> },
    { accessorKey: "status", header: "Status", cell: ({ row }) => { const s = QUOTE_STATUS[row.original.status] ?? QUOTE_STATUS.draft; return <span className={pill(s.cls)}>{s.label}</span>; } },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Quotes" description="Proposals with priced lines, approval and versions" />
        <ListShell
          columns={columns}
          rows={list.rows}
          loading={list.loading}
          pagination={list.pagination}
          onPage={list.setPage}
          onPerPage={list.setPerPage}
          noun="quotes"
          onRowClick={(q) => navigate(`/quotes/${q.id}`)}
          grid={{
            id: "quotes",
            search: <SearchInput className="w-full sm:w-64" placeholder="Search number, title or account…" value={search} onChange={(e) => setSearch(e.target.value)} />,
            filters: (
              <label className="text-xs text-gray-600">Status
                <AppSelect className={`${toolbarInput} mt-1`} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">All statuses</option>
                  {Object.entries(QUOTE_STATUS).filter(([k]) => k !== "superseded" && k !== "expired").map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </AppSelect>
              </label>
            ),
            activeFilters: status ? 1 : 0,
            onClearFilters: () => setStatus(""),
            sortOptions: [
              { value: "-issue_date", label: "Newest issued" }, { value: "issue_date", label: "Oldest issued" },
              { value: "-total", label: "Highest total" }, { value: "total", label: "Lowest total" },
              { value: "expiry_date", label: "Expiring soonest" }, { value: "number", label: "Number" },
            ],
            sort, onSort: setSort,
            stats: [
              { label: "Total quotes", value: countText(counts.all), hint: "All versions you can see" },
              { label: "Awaiting approval", value: countText(counts.pending), tone: counts.pending ? "warn" : "default" },
              { label: "Sent to customers", value: countText(counts.sent) },
              { label: "Accepted", value: countText(counts.accepted), hint: "Including invoiced", tone: "good" },
            ],
            statsLoading: countsLoading,
            exportEntity: "quotes", exportPermission: "quotes.export",
          }}
          onAdd={can("quotes.create") ? () => navigate("/quotes/new") : undefined}
          addLabel="New Quote"
          empty={{ icon: FileText, title: "No quotes yet", text: "Create a quote from your catalogue, send it, and turn it into an invoice once accepted.", action: "Create First Quote" }}
        />
      </div>
    </div>
  );
}
